export const SECOND_IN_MILLISECONDS = 1000;
export const MINUTE_IN_MILLISECONDS = 60 * SECOND_IN_MILLISECONDS;
export const HOUR_IN_MILLISECONDS = 60 * MINUTE_IN_MILLISECONDS;
export const DAY_IN_MIILISECONDS = 24 * HOUR_IN_MILLISECONDS;

export function getLocalTimezoneOffset() {
    return (new Date(0)).getTimezoneOffset() * MINUTE_IN_MILLISECONDS;
}

/**
 * Gets a timezone-agnostic unix timestamp representing the moment in time represented by the given arguments
 * @param year 
 * @param month 
 * @param day 
 * @param hour 
 * @param minute 
 * @param second 
 * @param millisecond 
 * @returns number of milliseconds since the Unix Epoch, without any alteration for time zone
 */
export function getUnixTimestamp(year: number, month: number = 0, day: number = 1,
    hour: number = 0, minute: number = 0, second: number = 0, millisecond: number = 0): number {
    const date = new Date(year, month, day, hour, minute, second, millisecond);
    return toUnixTimestamp(date);
}

/**
 * Gets a Date object that represents the time elapsed since the Unix epoch in some arbitrary time zone.
 * The object returned is to be interpreted as time zone agnostic.
 * @param unixTimestamp 
 * @returns 
 */
export function toDate(unixTimestamp: number): Date {
    const d = new Date(unixTimestamp);
    return new Date(
        d.getUTCFullYear(),
        d.getUTCMonth(),
        d.getUTCDate(),
        d.getUTCHours(),
        d.getUTCMinutes(),
        d.getUTCSeconds(),
        d.getUTCMilliseconds());
}

/**
 * Converts the given UTC timestamp into a Date object representing the equivalent local time
 * @param unixTimestamp Timestamp, representing a UTC date/time
 * @returns 
 */
export function toLocalDate(unixTimestamp: number): Date {
    return new Date(unixTimestamp);
}

/**
 * Converts the given UTC timestamp into a timestamp representing the equivalent local time
 * @param unixTimestamp Timestamp, representing a UTC date/time
 * @returns 
 */
export function toLocalUnixTimestamp(unixTimestamp: number) {
    const local = new Date(unixTimestamp);
    const utc = new Date();
    utc.setUTCFullYear(local.getFullYear());
    utc.setUTCMonth(local.getMonth());
    utc.setUTCDate(local.getDate());
    utc.setUTCHours(local.getHours());
    utc.setUTCMinutes(local.getMinutes());
    utc.setUTCSeconds(local.getSeconds());
    utc.setUTCMilliseconds(local.getMilliseconds());
    return utc.getTime();
}

/**
 * Converts the given local timestamp into a timestamp representing the equivalent UTC time
 * @param localTimestamp Timestamp, representing a local date/time
 * @returns 
 */
export function toUtcUnixTimestamp(localTimestamp: number) {
   const d = toDate(localTimestamp);
   return d.getTime();
}

export function getNowUnixTimestamp() {
    return toUnixTimestamp(new Date());
}

export function getUtcNowUnixTimestamp() {
    return (new Date()).getTime();
}

export function toUnixTimestamp(date: Date): number {
    const utc = new Date();
    utc.setUTCFullYear(date.getFullYear());
    utc.setUTCMonth(date.getMonth());
    utc.setUTCDate(date.getDate());
    utc.setUTCHours(date.getHours());
    utc.setUTCMinutes(date.getMinutes());
    utc.setUTCSeconds(date.getSeconds());
    utc.setUTCMilliseconds(date.getMilliseconds());
    return utc.getTime();
}

export function toDateString(unixTimestamp: number): string {
    const d = toDate(unixTimestamp);
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}

export function toAmPmTimeString(unixTimestamp: number): string {
    const d = toDate(unixTimestamp);
    const formatNumber = (n: number) => n < 10 ? `0${n}` : `${n}`;
    const hourMeridian = d.getHours() < 12 ?
        { h: d.getHours() === 0 ? 12 : d.getHours(), m: 'am' } : { h: d.getHours() - 12, m: 'pm' };

    return `${hourMeridian.h}:${formatNumber(d.getMinutes())}${hourMeridian.m}`;
}

export function toDateTimeString(unixTimestamp: number): string {
    return `${toDateString(unixTimestamp)} @${toAmPmTimeString(unixTimestamp)}`;
}

function isNumber(n: any): n is number {
    return isNaN(parseFloat(n));
}
type DateTimestamp = Date | number;
function guardTimespanDiffFunction(functionName: string, a: DateTimestamp, b: DateTimestamp) {
    if(isNumber(a) !== isNumber(b)) { throw Error(`Illegal usage of function ${functionName}.`)}
}
function getTimestampDiffParams(a: DateTimestamp, b: DateTimestamp): { min: number, max: number } {
    let n1: number;
    let n2: number;
    if(!isNumber(a)) {
        n1 = toUnixTimestamp(a as Date);
        n2 = toUnixTimestamp(b as Date);
    } else {
        n1 = a as number;
        n2 = b as number;
    }

    if(n1 < n2) {
        return { min: n1, max: n2 };
    } else {
        return { min: n2, max: n1 };
    }
}
function getDateDiffParams(a: DateTimestamp, b: DateTimestamp): { min: Date, max: Date } {
    let d1: Date;
    let d2: Date;
    if(isNumber(a)) {
        d1 = toDate(a as number);
        d2 = toDate(b as number);
    } else {
        d1 = a as Date;
        d2 = b as Date;
    }

    if(d1.getTime() < d2.getTime()) {
        return { min: d1, max: d2 };
    } else {
        return { min: d2, max: d1 };
    }
}
function getDateNormalizedToYear(d: Date) {
    const firstDayOfYear = new Date(d.getFullYear(), 0, 1);
    const daysInYear = getTimespan_days(new Date(d.getFullYear() + 1, 0, 1), firstDayOfYear);
    const daysElapsed = getTimespan_days(d, firstDayOfYear);

    return daysElapsed / daysInYear;
}
function getDateNormalizedToMonth(d: Date, absolutePrecision: boolean) {
    const firstDayOfMonth = new Date(d.getFullYear(), d.getMonth(), 1);
    const daysInMonth = getTimespan_days(new Date(d.getFullYear(), d.getMonth() + 1, 1), firstDayOfMonth);
    const daysElapsed = getTimespan_days(d, firstDayOfMonth);

    if(absolutePrecision) {
        return daysElapsed / daysInMonth;
    }
    else if(daysElapsed < 15) {
        return (daysElapsed / 15) * 0.5;
    }
    else {
        return 0.5 + (((daysElapsed - 15) / (daysInMonth - 15)) * 0.5);
    }
}

export function getTimespan_years(a: Date, b: Date): number;
export function getTimespan_years(a: number, b: number): number;
export function getTimespan_years(a: DateTimestamp, b: DateTimestamp): number {
    guardTimespanDiffFunction('getTimespan_years', a, b);
    const diff = getDateDiffParams(a, b);

    const max = (diff.max.getFullYear() - diff.min.getFullYear()) + getDateNormalizedToYear(diff.max);
    const min = getDateNormalizedToYear(diff.min);
    return max - min;
}

export function getTimespan_months(a: Date, b: Date, absolutePrecision: boolean): number;
export function getTimespan_months(a: number, b: number, absolutePrecision: boolean): number;
export function getTimespan_months(a: DateTimestamp, b: DateTimestamp, absolutePrecision: boolean = false): number {
    guardTimespanDiffFunction('getTimespan_months', a, b);
    const diff = getDateDiffParams(a, b);

    const max = ((diff.max.getFullYear() - diff.min.getFullYear()) * 12) + diff.max.getMonth() + getDateNormalizedToMonth(diff.max, absolutePrecision);
    const min = diff.min.getMonth() + getDateNormalizedToMonth(diff.min, absolutePrecision);
    return max - min;
}

export function getTimespan_days(a: Date, b: Date): number;
export function getTimespan_days(a: number, b: number): number;
export function getTimespan_days(a: DateTimestamp, b: DateTimestamp): number {
    guardTimespanDiffFunction('getTimespan_days', a, b);
    const diff = getTimestampDiffParams(a, b);
    return (diff.max - diff.min) / DAY_IN_MIILISECONDS;
}

export function getTimespan_hours(a: Date, b: Date): number;
export function getTimespan_hours(a: number, b: number): number;
export function getTimespan_hours(a: DateTimestamp, b: DateTimestamp): number {
    guardTimespanDiffFunction('getTimespan_hours', a, b);
    const diff = getTimestampDiffParams(a, b);
    return (diff.max - diff.min) / HOUR_IN_MILLISECONDS;
}

export function getTimespan_minutes(a: Date, b: Date): number;
export function getTimespan_minutes(a: number, b: number): number;
export function getTimespan_minutes(a: DateTimestamp, b: DateTimestamp): number {
    guardTimespanDiffFunction('getTimespan_minutes', a, b);
    const diff = getTimestampDiffParams(a, b);
    return (diff.max - diff.min) / MINUTE_IN_MILLISECONDS;
}

export function getTimespan_seconds(a: Date, b: Date): number;
export function getTimespan_seconds(a: number, b: number): number;
export function getTimespan_seconds(a: DateTimestamp, b: DateTimestamp): number {
    guardTimespanDiffFunction('getTimespan_seconds', a, b);
    const diff = getTimestampDiffParams(a, b);
    return (diff.max - diff.min) / SECOND_IN_MILLISECONDS;
}

export function getTimespan_milliseconds(a: Date, b: Date): number;
export function getTimespan_milliseconds(a: number, b: number): number;
export function getTimespan_milliseconds(a: DateTimestamp, b: DateTimestamp): number {
    guardTimespanDiffFunction('getTimespan_milliseconds', a, b);
    const diff = getTimestampDiffParams(a, b);
    return diff.max - diff.min;
}
